import React, { ForwardedRef } from 'react';
import { useTranslation } from 'react-i18next';
import InvalidFeedback from './invalid-feedback.component';

interface AdvanceRadioProps {
  options: {
    value: string;
    title: string;
    description?: string;
    icon: React.ReactNode;
    bgClass?: string;
    badge?: string;
    type?: string;
  }[];
  errors?: any;
  value: string;
  label?: string;
  required?: boolean;
  optional?: boolean;
  onChange: (value: string) => void;
}

const AdvanceRadio = React.forwardRef<HTMLInputElement, AdvanceRadioProps>(
  (
    {
      label,
      options,
      errors,
      value,
      required = false,
      optional = false,
      onChange
    }: AdvanceRadioProps,
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const { t } = useTranslation();

    return (
      <div className="mb-7">
        {label && (
          <label className={`form-label mb-4 ${required ? 'required' : ''}`}>
            {label}
            {optional && <span className="fs-8 text-muted ms-1">({t('general.optional')})</span>}
          </label>
        )}

        <div className="d-flex flex-wrap gap-4 mb-6">
          {options.map((option, index) => {
            const isSelected = value === option.value;
            const id = `adv-radio-${option.value}`;
            const cardTypeClass = option.type ?? 'border-light';
            return (
              <div
                key={option.value}
                className={`cardC-checkbox card-hover-n ${isSelected ? 'cardRadioBg' : ''}`}
              >
                <input
                  type="radio"
                  id={id}
                  name="advance-radio"
                  value={option.value}
                  checked={isSelected}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    if (newValue !== value) {
                      const audio = new Audio('/assets/media/audio/onSelected.mp3');
                      audio.play();
                      onChange(newValue);
                    }
                  }}
                  ref={ref}
                  className="position-absolute top-0 start-0 opacity-0 w-100 h-100"
                  style={{ zIndex: 1 }}
                />
                <label
                  htmlFor={id}
                  className={`cardC animate-fadeInUp animate-delay-${index} ${cardTypeClass}`}
                >
                  <span className={`check-icon ${isSelected ? 'cardiconCheck' : ''}`}>✔</span>

                  <div className="p-4 d-flex justify-content-center">
                    <img
                      src={`/assets/media/logos/${option.value}-logo.png`}
                      alt=""
                      width={'45%'}
                    />
                  </div>
                  <div className="cardC-content py-3 px-2">
                    <h2 className="fw-bold mb-1 text-center">{option.title}</h2>
                    {option.description && <p className="mb-0">{option.description}</p>}
                    {option.badge && <span className="badge mt-2 d-block">{option.badge}</span>}
                  </div>
                </label>
              </div>
            );
          })}
        </div>

        <span className="text-danger mb-3 d-block">{errors?.message}</span>
        <InvalidFeedback error={errors} />
      </div>
    );
  }
);

export default AdvanceRadio;
